import React from "react";
import { Box, Typography } from "@mui/material";
import { looksLikeHtml, sanitizeHtml } from "../utils/sanitizeHtml";
import { richContentStyles } from "./RichTextEditor";

interface FormattedTextBlockProps {
  text: string;
  color?: string;
}

type InlineToken =
  | { type: "text"; value: string }
  | { type: "italic"; value: string }
  | { type: "bold"; value: string };

function parseInline(text: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let lastIndex = 0;

  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    const raw = match[0];

    if (start > lastIndex) {
      tokens.push({ type: "text", value: text.slice(lastIndex, start) });
    }

    if (raw.startsWith("**") && raw.endsWith("**")) {
      tokens.push({ type: "bold", value: raw.slice(2, -2) });
    } else if (raw.startsWith("*") && raw.endsWith("*")) {
      tokens.push({ type: "italic", value: raw.slice(1, -1) });
    } else {
      tokens.push({ type: "text", value: raw });
    }

    lastIndex = start + raw.length;
  }

  if (lastIndex < text.length) {
    tokens.push({ type: "text", value: text.slice(lastIndex) });
  }

  return tokens;
}

function renderInline(text: string) {
  return parseInline(text).map((token, index) => {
    if (token.type === "bold") {
      return <Box key={index} component="strong" sx={{ display: "inline" }}>{token.value}</Box>;
    }
    if (token.type === "italic") {
      return <Box key={index} component="em" sx={{ display: "inline" }}>{token.value}</Box>;
    }
    return <React.Fragment key={index}>{token.value}</React.Fragment>;
  });
}

export default function FormattedTextBlock({ text, color = "inherit" }: FormattedTextBlockProps) {
  if (looksLikeHtml(text)) {
    return (
      <Box
        sx={{ color, lineHeight: 1.7, wordBreak: "break-word", ...richContentStyles }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }}
      />
    );
  }

  const blocks = text
    .split(/\r?\n\s*\r?\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  return (
    <Box>
      {blocks.map((block, blockIndex) => {
        const headingMatch = block.match(/^(#{1,3})\s+(.+)$/);

        if (headingMatch) {
          const level = headingMatch[1].length;
          const variant = level === 1 ? "h6" : level === 2 ? "subtitle1" : "subtitle2";

          return (
            <Typography
              key={blockIndex}
              variant={variant}
              sx={{
                mb: blockIndex === blocks.length - 1 ? 0 : 1,
                fontWeight: 700,
                color,
              }}
            >
              {renderInline(headingMatch[2])}
            </Typography>
          );
        }

        return (
          <Typography
            key={blockIndex}
            variant="body1"
            sx={{
              mb: blockIndex === blocks.length - 1 ? 0 : 1.5,
              lineHeight: 1.7,
              color,
              whiteSpace: "pre-wrap",
            }}
          >
            {renderInline(block)}
          </Typography>
        );
      })}
    </Box>
  );
}